// src/helpers/searchTrending.ts
import { TrendingBucket, TrendingItem } from "../api/analyticsAdmin";
import { SEGMENT_COLORS } from "./searchTrend";

export const latestBucket = (
  buckets: TrendingBucket[]
): TrendingBucket | null =>
  buckets.length ? buckets[buckets.length - 1] : null;

export type DeltaTone = "new" | "up" | "down" | "flat";

export interface DeltaInfo {
  tone: DeltaTone;
  label: string;
}

// delta carries its own sign; deltaPct is non-null whenever !isNew && delta!==0.
export const deltaInfo = (item: TrendingItem): DeltaInfo => {
  if (item.isNew) return { tone: "new", label: "NEW" };
  if (item.delta === 0) return { tone: "flat", label: "0" };
  const pct = Math.round((item.deltaPct ?? 0) * 100);
  if (item.delta > 0) return { tone: "up", label: `+${item.delta} (+${pct}%)` };
  return { tone: "down", label: `${item.delta} (${pct}%)` };
};

export interface BumpSeries {
  key: string;
  label: string;
  color: string;
}

export interface BumpRow {
  bucketStart: string;
  [query: string]: number | string | null;
}

export interface BumpChart {
  rows: BumpRow[];
  series: BumpSeries[];
}

// Lines = the newest bucket's top-N queries, traced backward across every bucket.
export const buildBumpChart = (
  buckets: TrendingBucket[],
  topN: number
): BumpChart => {
  const newest = latestBucket(buckets);
  const leaders = newest ? newest.items.slice(0, topN) : [];
  const series: BumpSeries[] = leaders.map((it, i) => ({
    key: it.query,
    label: it.displayQuery ?? it.query,
    color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
  }));

  const rows: BumpRow[] = buckets.map((bucket) => {
    const row: BumpRow = { bucketStart: bucket.bucketStart };
    series.forEach((s) => {
      const found = bucket.items.find((it) => it.query === s.key);
      row[s.key] = found ? found.rank : null;
    });
    return row;
  });

  return { rows, series };
};
